import $ from 'jquery';
import _ from 'lodash';
import Parallel from 'parallel';


const defaultConfig = {
    fromColour: "#000",
    toColour: "#fff"
};

const colorizeThemes = {
    'article-artemis-blue': {
        from: '#7AA9D4',
        to: '#fdfaef'
    },
    'article-oxford-blue': {
        from: '#00112e',
        to: '#fdfaef'
    },
    'article-bronze': {
        from: '#a27f5c',
        to: '#fdfaef'
    },
    'article-bronze-patina': {
        from: '#9CC3C5',
        to:'#fdfaef'
    },
    'article-forest': {
        from: '#687646',
        to: '#fdfaef'
    },
    'article-rust': {
        from: '#d67c40',
        to: '#fdfaef'
    },
    'article-violet': {
        from: '#756286',
        to:'#fdfaef'
    },
    'article-cream': {
        from: '#fdfaef',
        to: '#ffffff'
    },
    'article-powder-grey': {
        from: '#f2f2f2',
        to: '#ffffff'
    },
};

const filtersSvgContainerId = 'colorizeFilters';

const getFilterId = (from, to) => {
    const colorsPart = `${from}_${to}`.replace(/#/g, '').toLowerCase();
    return `${filtersSvgContainerId}_${colorsPart}`;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

const RGB_INDICES = [0, 1, 2];
const LUMINANCE_COEFFS = [0.299, 0.587, 0.114];

function parseHexColor(str) {
    const start = str[0] === '#' ? 1 : 0;
    const len = str.length - start;
    if (len !== 3 && len !== 6) throw new Error(`Color ${str} has invalid length`);
    const step = len / 3;
    const maxValue = ((1 << 4 * step) - 1);
    return RGB_INDICES.map((i) => parseInt(str.slice(i * step + start, (i + 1) * step + start), 16) / maxValue);
}

function getColorizeMatrix(from, to) {
    const matrix = [];
    // RGB out = Interpolate between `from` and `to` using luminance as distance [0, 1]
    for (const i in RGB_INDICES) {
        const range = to[i] - from[i];
        matrix.push(...LUMINANCE_COEFFS.map((lc) => lc * range), /* Alpha in */ 0, /* Offset */ from[i]);
    }
    // Alpha out = Alpha in
    matrix.push(0, 0, 0, 1, 0); 
    return matrix;
}

function createColorizeFilterElement(elementId, fromStr, toStr) {
    const from = parseHexColor(fromStr);
    const to = parseHexColor(toStr);
    const matrixValues = getColorizeMatrix(from, to).map(x => x.toPrecision(3)).join(' ');
    const colorMatrixElement = document.createElementNS(SVG_NS, 'feColorMatrix');
    colorMatrixElement.setAttribute('in', 'SourceGraphic');
    colorMatrixElement.setAttribute('type', 'matrix');
    colorMatrixElement.setAttribute('values', matrixValues);
    colorMatrixElement.setAttribute('color-interpolation-filters', 'sRGB');
    const filterElement = document.createElementNS(SVG_NS, 'filter');
    filterElement.setAttribute('id', elementId);
    filterElement.appendChild(colorMatrixElement);
    return filterElement;
}

function getColorizeFilter(from, to) {
    const filterId = getFilterId(from, to);
    
    if (!document.getElementById(filterId)) {
        let svgElement = document.getElementById(filtersSvgContainerId);
        if (!svgElement) {
            svgElement = document.createElementNS(SVG_NS, 'svg');
            svgElement.setAttribute('id', filtersSvgContainerId);
            svgElement.setAttribute('width', '0');
            svgElement.setAttribute('height', '0');
            document.body.appendChild(svgElement);
        }
        svgElement.appendChild(createColorizeFilterElement(filterId, from, to));
    }

    return filterId;
}

export class ColorizedImage {

    constructor(target, config = {}) {

        /**
         *
         * @type {string | HTMLElement}
         */
        if (typeof target === 'string') {
            this.target = document.querySelector(target);
            if (!this.target) {
                console.error(`ColorizedImage: target not found`, target);
                return;    
            }
        } else {
            this.target = target;
            if (!(this.target instanceof HTMLElement)) {
                console.error(`ColorizedImage: target is not a HTMLElement`, target);
                return;
            }
        }

        /**
         * @type {Object}
         */
        this.config = _.defaults(config, defaultConfig);

        /**
         * @type {string}
         */
        this._fromColour = this.target.dataset.colorizeFrom || this.config.fromColour;

        /**
         * @type {string}
         */
        this._toColour = this.target.dataset.colorizeTo || this.config.toColour;

        this._theme = this.target.dataset.colorizeTheme || this.config.theme;
        if(this._theme){
          if(colorizeThemes[this._theme]){
            this._fromColour = colorizeThemes[this._theme].from;
            this._toColour = colorizeThemes[this._theme].to;
          } else {
            //Theme specified, but not found, so lets pick one at random
            let key = _.first(_.shuffle(_.keys(colorizeThemes)))
            this._fromColour = colorizeThemes[key].from;
            this._toColour = colorizeThemes[key].to;
          }
        }

        this.init();
    }

    /**
     *
     */
    init() {
        const filterId = getColorizeFilter(this._fromColour, this._toColour);
        const originalFilterStyle = this.target.style.filter;
        
        if (originalFilterStyle.indexOf(filterId) === -1) {
            this.target.style.filter = `${originalFilterStyle} url(#${filterId})`;
        }
    }
}

/**
 *
 * @type {Map}
 */
ColorizedImage.srcMap = new Map();

/**
 *
 * @param selector
 * @returns {ColorizedImage[]}
 */
ColorizedImage.applyToElements = function(selector, config){
    var a = [];
    $(selector).each(function(){
        a.push(new ColorizedImage(this, config));
    });

    return a;
};


/*
 |--------------------------------------------------------------------------
 | Background image
 |--------------------------------------------------------------------------
 |
 */
export class ColorizedBackgroundImage {

    constructor(target, config = {}) {

        if(!this.isCanvasSupported()){
            return;
        }

        /**
         *
         * @type {jQuery}
         */
        this.$target = $(target).eq(0); //Ensure only one element

        /**
         * @type {Object}
         */
        this.config = _.defaults(config, defaultConfig);

        /**
         *
         * @type {Element}
         * @private
         */
        this._canvas = document.createElement('canvas');

        /**
         *
         * @type {*|CanvasRenderingContext2D}
         * @private
         */
        this._ctx = this._canvas.getContext('2d');

        /**
         *
         * @type {Element}
         * @private
         */
        this._img = document.createElement('img');

        /**
         * @type {number}
         */
        this._width = this.$target.width();

        /**
         * @type {number}
         */
        this._height = this.$target.height();

        /**
         * @type {string}
         */
        this._fromColour = (this.$target.data('colorize-from') || this.config.fromColour);

        /**
         * @type {string}
         */
        this._toColour = (this.$target.data('colorize-to') || this.config.toColour);

        this._theme =  (this.$target.data('colorize-theme') || this.config.theme);
        if(this._theme){
          if(colorizeThemes[this._theme]){
            this._fromColour = colorizeThemes[this._theme].from;
            this._toColour = colorizeThemes[this._theme].to;
          } else {
            //Theme specified, but not found, so lets pick one at random
            let key = _.first(_.shuffle(_.keys(colorizeThemes)));
            this._fromColour = colorizeThemes[key].from;
            this._toColour = colorizeThemes[key].to;
          }
        }


        this.init();
    }

    /**
     *
     */
    init() {
        var src = this.$target.css('background-image').replace(/^url\(["']?/, '').replace(/["']?\)$/, '');
        var srcMetaKey = src + this._width + this._height + this._fromColour.trim() + this._toColour.trim();

        // Is another element already colorizing this image? If so it will store a promise in the meta element. If
        // it doesnt exist, kick off a colorise process and create the promise.
        if (!ColorizedImage.srcMap.has(srcMetaKey)) {
            ColorizedImage.srcMap.set(srcMetaKey, new Promise((resolve, reject) => {
                $(this._img).on('load', resolve);
            }).then(this.onImgReady.bind(this)));

            this._img.src = src;
        }

        ColorizedImage.srcMap.get(srcMetaKey).then(this.onPixelsReady.bind(this));

        this.$target.css('background-image', 'none');
    }

    /**
     *
     * @returns {Promise}
     */
    onImgReady() {
        this._canvas.width = this._img.width;
        this._canvas.height = this._img.height;

        this._ctx.drawImage(this._img, 0, 0, this._canvas.width, this._canvas.height);

        var worker = new Parallel(this._ctx.getImageData(0, 0, this._canvas.width, this._canvas.height), {
            evalPath: '/Assets/built/libs/paralleljs/lib/eval.js',
            env: {
                origin: location.origin || (location.protocol + "//" + location.host  ),
                from: this._fromColour,
                to: this._toColour
            }
        });
        return new Promise((resolve, reject) => {
            //IE9 needs this since web workers arent supported, hence no global object.
            var env = {
                origin: location.origin || (location.protocol + "//" + location.host  ),
                from: this._fromColour,
                to: this._toColour
            };

            worker.spawn(function (pixels) {

                //Import the chroma.js when in a worker
                try{
                    // When import scripts from inside a worker created as an inline script (like here using Parallel.js)
                    // you must use a FULL URL path since this worker is created on the fly using the blob: protocol
                    // and relative URLs will be relative to the blob: path.
                    // See here for more info: http://www.html5rocks.com/en/tutorials/workers/basics/#toc-inlineworkers
                    importScripts(global.env.origin + '/Assets/built/libs/chroma-js/chroma.min.js');
                } catch(e) {
                    //Fallback to non web worker, hence the chroma.js is already loaded
                    window.global = {
                        env: env
                    }
                }

                var l, p, scale = chroma.scale([global.env.from, global.env.to]);

                for (var i = 0; i < pixels.data.length; i += 4) {
                    l = 0.299 * pixels.data[i] + 0.587 * pixels.data[i + 1] + 0.114 * pixels.data[i + 2];
                    p = scale(l / 255).rgb();
                    pixels.data[i] = p[0];
                    pixels.data[i + 1] = p[1];
                    pixels.data[i + 2] = p[2];
                    pixels.data[i + 3] = pixels.data[i+3];
                }
                return pixels;
            }).then(resolve);
        })
    }

    /**
     *
     * @param imageData
     */
    onPixelsReady(imageData) {
        //Redundant in many cases, but when processing the same image in multiple elements on a page, the
        // promise flow above means that the canvas image size will not be set yet.
        this._canvas.width = imageData.width;
        this._canvas.height = imageData.height;
        this._ctx.putImageData(imageData, 0, 0);
        this.$target.css('background-image', `url(${this._canvas.toDataURL()})`);
    }

    /**
     *
     * @returns {boolean}
     */
    isCanvasSupported(){
        var elem = document.createElement('canvas');
        return !!(elem.getContext && elem.getContext('2d'));
    }

}

/**
 *
 * @type {Map}
 */
ColorizedBackgroundImage.srcMap = new Map();

/**
 *
 * @param selector
 * @param config
 * @returns {ColorizedBackgroundImage[]}
 */
ColorizedBackgroundImage.applyToElements = function(selector, config){
    var a = [];
    $(selector).each(function(){
        a.push(new ColorizedBackgroundImage(this, config));
    });

    return a;
};
